import { ChangeDetectionStrategy, Component, computed, effect, HostListener, inject, input, OnDestroy, output, signal, ViewEncapsulation } from '@angular/core';
import { GlobalTablesService } from '@src/suite/common/services/global-tables-service';
import { NomWebServicesJson } from '@src/suite/common/services/nom-web-services-json';
import { selectCollaborativeEditingAPIKey } from '@src/suite/common/store/global-tables/global-tables.selectors';
import { FroalaEditorModule } from 'angular-froala-wysiwyg';
import FroalaEditor from 'froala-editor';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

import { EditorActions } from '../../../enums/enums';
import { ProductionCommandType } from '../../../utils/ml-2-html-utils';
import { StoryTextEditorBase } from '../st-story-text-editor.base';

declare const Codox: any;

const CODOX_RETRY_TIMEOUT_MS = 5000;
const MAX_CODOX_RETRIES = 3;
const CONTENT_CHANGE_DEBOUNCE_MS = 300;

@Component({
    selector: 'st-story-collaboration-text-editor',
    imports: [FroalaEditorModule],
    templateUrl: './st-story-collaboration-text-editor.html',
    styleUrl: './st-story-collaboration-text-editor.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: {
        class: 'st-story-collaboration-text-editor',
    },
})
export class StoryCollaborationTextEditorComponent extends StoryTextEditorBase implements OnDestroy {
    private readonly nomWebServicesJson = inject(NomWebServicesJson);
    private readonly globalTablesService = inject(GlobalTablesService);

    // Additional input (disabled and text are inherited from base)
    readonly docId = input<string>('');

    // Computed
    private readonly codoxApiKey = computed(() => this.store.selectSignal(selectCollaborativeEditingAPIKey)());
    readonly collaborationEnabled = computed(() => this.codoxApiKey() && this.docId());

    // Additional outputs
    readonly collabUsers = output<{ id: string }[]>();
    // Emits true when a Codox session is being established (editor locked, waiting for
    // usersUpdate), false when Codox connects successfully or permanently gives up.
    // Allows the parent to surface a loading indicator during the initialisation window.
    readonly codoxInitializing = output<boolean>();
    // Emits once when Codox permanently fails to connect after all retries are exhausted.
    readonly codoxFailed = output<void>();

    // Content debounce pipeline — collab edits arrive via Codox hook so we debounce before emitting
    private readonly contentChange$ = new Subject<string>();

    // Codox state
    private codoxPlugin: typeof Codox = null;
    private readonly codoxAvailable = signal<boolean>(false);
    private readonly codoxFailToInitialize = signal<boolean>(false);
    private retryTimeout: ReturnType<typeof setTimeout> | null = null;
    private retryCount = 0;
    // Tracks the docId that the currently-running Codox session was started with,
    // so the lifecycle effect can detect when a story change requires a restart.
    private runningDocId: string | null = null;
    // Holds the original console.error reference while the interceptor is active so it
    // can be restored when the Codox session ends. See setupConsoleErrorInterceptor().
    private originalConsoleError: ((...args: unknown[]) => void) | null = null;
    // Gates the fallback Froala contentChanged handler. The handler itself is registered
    // once (when the listener is first installed) and persists on the editor instance for
    // the component's lifetime; this flag enables/disables it without touching Froala's
    // event system. Set to true by installFallbackContentChangedListener(), cleared by
    // removeFallbackContentChangedListener() and stopCollaboration().
    private isFallbackListenerActive = false;

    readonly isEditorDisabled = computed(() => this.disabled() || (this.collaborationEnabled() && !this.codoxAvailable() && !this.codoxFailToInitialize()));

    protected override isEditorEffectivelyDisabled(): boolean {
        return this.isEditorDisabled();
    }

    readonly froalaOptions = computed(() => ({
        key: this.editorApiKey(),
        ...this.commonFroalaConfig,
        events: {
            ...this.commonFroalaConfig.events,
            initialized: (e: FroalaEditor) => {
                const editorInstance = e.getEditor();
                if (!editorInstance) return;

                this.editor.set(editorInstance);

                // Always start disabled — the reactive effect in the base class calls edit.on()
                // once the editor is truly ready (Codox connected, or collab not configured).
                // This closes the timing window where the store hasn't delivered the API key yet,
                // making collaborationEnabled() transiently false and leaving the editor open.
                editorInstance.edit.off();

                // Froala's built-in toolbar is always hidden — the custom st-story-toolbar replaces it.
                editorInstance.toolbar.hide();

                editorInstance.events.on(EditorActions.SAVE, () => this.save.emit(), true);

                this.applyCommonListeners(editorInstance);
            },
        },
    }));

    constructor() {
        super();

        this.contentChange$.pipe(debounceTime(CONTENT_CHANGE_DEBOUNCE_MS), takeUntil(this.destroy$)).subscribe(html => {
            if (this.isProcessingContentChange) {
                return;
            }
            if (!this.userHasInteracted) {
                this.contentChanged.emit(html);
                return;
            }
            this.userHasInteracted = false;
            this.confirmApprovedStoryEdit(() => this.contentChanged.emit(html));
        });

        // Manage the Codox lifecycle reactively so it mirrors the editor's enabled state
        // across the full component lifetime — not just at initialisation.
        // codoxPlugin and runningDocId are plain properties (not signals), so reading them
        // here is a non-tracked snapshot. The effect re-runs when editor(), disabled(), or
        // collaborationEnabled() change. Because collaborationEnabled() depends on docId(),
        // navigating to a new story also triggers this effect, allowing the session to be
        // stopped and restarted for the new docId.
        effect(() => {
            if (!this.editor() || !this.collaborationEnabled()) return;

            if (this.disabled()) {
                // Editor became disabled — drop the collaboration session so the user does
                // not appear as an active collaborator while they cannot edit.
                if (this.codoxPlugin) {
                    this.stopCollaboration();
                }
            } else {
                // Start if no session is running, or restart if the active session belongs
                // to a different docId (i.e. the user navigated to a new story).
                if (!this.codoxPlugin || this.runningDocId !== this.docId()) {
                    // Reset the retry budget here, not in stopCollaboration(). stopCollaboration()
                    // is called from inside initialize() on every retry attempt, so resetting
                    // there would make MAX_CODOX_RETRIES ineffective. Resetting here ensures a
                    // fresh budget only when a genuinely new session is being requested.
                    this.retryCount = 0;
                    // Clear any previous failure state so the new session starts fresh and the
                    // editor re-locks while Codox is connecting (isEditorDisabled depends on this).
                    this.codoxFailToInitialize.set(false);
                    this.startCollaboration();
                }
            }
        });

        // When Codox fails permanently (codoxFailToInitialize), the editor becomes usable
        // (isEditorDisabled switches to false) but has no content listener — user edits would
        // be silently lost. This effect installs a direct Froala contentChanged listener that
        // routes through the same contentChange$ debounce pipeline as the Codox hook, giving
        // the editor basic (non-collaborative) content tracking as a fallback.
        //
        // The listener is implemented as a gate flag (isFallbackListenerActive) rather than
        // adding/removing Froala event handlers dynamically. The closure is registered once
        // and exits early when the gate is closed, avoiding any dependency on Froala's
        // events.off() API for selective handler removal.
        //
        // The gate is also closed by stopCollaboration() so that when a new Codox session
        // starts (e.g. on story navigation) the fallback handler goes silent immediately —
        // before Angular has scheduled this effect to re-run — preventing a race where both
        // the Codox hook and the fallback listener are active simultaneously.
        effect(() => {
            const failed = this.codoxFailToInitialize();
            const ed = this.editor();

            if (failed && ed) {
                this.installFallbackContentChangedListener(ed);
            } else {
                this.removeFallbackContentChangedListener();
            }
        });

        effect(() => {
            if (this.codoxFailToInitialize()) {
                this.codoxFailed.emit();
            }
        });
    }

    override ngOnDestroy(): void {
        super.ngOnDestroy();
        this.stopCollaboration();
        this.codoxInitializing.emit(false);
    }

    protected emitContentChange(html: string): void {
        if (this.codoxPlugin) {
            // Normal collaborative mode: push the change through Codox so all collaborators
            // receive the update (e.g. after a production command or MOS item is inserted).
            this.codoxPlugin.forceSync(html);
        } else {
            // Fallback mode (codoxFailToInitialize): Codox is gone, emit directly like the
            // basic editor does, so production command / MOS changes are not silently lost.
            this.contentChanged.emit(html);
        }
    }

    startCollaboration(): void {
        try {
            if (!this.editor()?.el || !this.codoxApiKey() || !this.docId()) {
                return;
            }

            const userName = this.globalTablesService.getUserNameByIDV2(this.nomWebServicesJson.UserId);

            const initialize = () => {
                this.stopCollaboration();
                this.setupConsoleErrorInterceptor();
                this.codoxPlugin = new Codox();
                this.runningDocId = this.docId();
                console.warn('StoryCollaborationTextEditor: Starting Codox for docId:', this.docId());

                this.codoxPlugin.init({
                    app: 'froala',
                    domain: 'conquex.com',
                    user: { id: this.nomWebServicesJson.UserId, name: userName },
                    docId: this.docId(),
                    apiKey: this.codoxApiKey(),
                    editor: this.editor(),
                    showUI: false,
                    showCursor: true,
                    cursorAlwaysOn: true,
                    hooks: {
                        usersUpdate: (data: { id: string }[]) => {
                            if (!this.codoxAvailable()) {
                                console.warn('StoryCollaborationTextEditor: Codox initialized for docId:', this.docId());
                                this.codoxAvailable.set(true);
                                this.codoxInitializing.emit(false);
                            }
                            if (this.retryTimeout) {
                                clearTimeout(this.retryTimeout);
                                this.retryTimeout = null;
                            }

                            this.collabUsers.emit(data);
                        },
                        contentChanged: () => {
                            this.contentChange$.next(this.editor()?.html?.get() ?? '');
                        },
                    },
                });

                // Codox's init() internally calls editor.edit.on() before the connection is
                // established. Re-disable immediately so the editor stays locked until the
                // usersUpdate hook fires and codoxAvailable becomes true.
                this.editor()?.edit.off();

                this.codoxPlugin.on('error', (data: { message?: string }) => {
                    console.error('StoryCollaborationTextEditor: Codox error:', data);
                });

                // Schedule a retry if Codox hasn't connected within the timeout window.
                // The timeout is placed here, inside initialize(), so that every attempt —
                // first or retry — gets its own window. If it were only in startCollaboration(),
                // retries triggered by the timeout itself would have no follow-up timeout,
                // leaving the editor locked permanently after just one failed retry.
                // stopCollaboration() (called at the top of initialize()) already clears any
                // pending retryTimeout, so there is no risk of double-scheduling.
                this.retryTimeout = setTimeout(() => {
                    this.retryTimeout = null;
                    if (!this.codoxAvailable() && this.retryCount < MAX_CODOX_RETRIES) {
                        this.retryCount++;
                        console.warn(`StoryCollaborationTextEditor: Codox not initialized, retrying (${this.retryCount}/${MAX_CODOX_RETRIES})...`);
                        initialize();
                    } else if (!this.codoxAvailable()) {
                        console.error('StoryCollaborationTextEditor: Codox failed to connect after max retries.');
                        // Stop the plugin before signalling failure so Codox's internal
                        // polling stops immediately and does not continue flooding the
                        // network with requests. codoxFailToInitialize.set() then makes
                        // isEditorDisabled() false so the base-class effect calls edit.on().
                        this.stopCollaboration();
                        this.codoxFailToInitialize.set(true);
                        this.codoxInitializing.emit(false);
                    }
                }, CODOX_RETRY_TIMEOUT_MS);
            };

            this.codoxInitializing.emit(true);
            initialize();
        } catch (error) {
            console.error('StoryCollaborationTextEditor: Codox initialization error:', error);
            this.codoxAvailable.set(false);
            this.codoxInitializing.emit(false);
        }
    }

    @HostListener('window:beforeunload')
    onBeforeUnload() {
        this.stopCollaboration();
    }

    stopCollaboration(): void {
        this.cleanupConsoleErrorInterceptor();
        // Silence the fallback listener immediately. This is synchronous, so even if the
        // fallback listener effect hasn't been scheduled yet, the Froala handler won't fire
        // after this point. Prevents a race where both Codox and the fallback listener emit.
        this.removeFallbackContentChangedListener();
        if (this.retryTimeout) {
            clearTimeout(this.retryTimeout);
            this.retryTimeout = null;
        }
        if (this.codoxPlugin) {
            this.codoxPlugin.stop();
            this.codoxPlugin = null;
        }
        this.runningDocId = null;
        this.codoxAvailable.set(false);
        this.collabUsers.emit([]);
        this.editor().edit.off();
    }

    /**
     * Patches console.error for the lifetime of a Codox session to intercept errors that
     * Codox emits directly to the console rather than through its error event. When such an
     * error is detected the current timeout-based retry is cancelled and a new session starts
     * immediately, rather than waiting the full CODOX_RETRY_TIMEOUT_MS before attempting.
     *
     * The interceptor is installed per session inside initialize() (after the previous session
     * has been torn down) and removed in stopCollaboration(), ensuring at most one override is
     * stacked at any time per component instance.
     *
     * Note: two component instances (above / below black line) each install their own
     * interceptor, forming a chain. Both are cleaned up when their respective sessions end.
     */
    private setupConsoleErrorInterceptor(): void {
        this.originalConsoleError = console.error as (...args: unknown[]) => void;
        console.error = (...args: unknown[]) => {
            this.originalConsoleError?.apply(console, args);

            const errorMessage = args.join(' ');
            const isCodoxError = errorMessage.includes('codox_handleMessage') || errorMessage.includes('ESTOP') || errorMessage.includes('wave.client.js');
            if (isCodoxError && !this.codoxAvailable() && !this.codoxFailToInitialize() && this.retryCount < MAX_CODOX_RETRIES) {
                this.retryCount++;
                console.warn(`StoryCollaborationTextEditor: Codox console error detected, retrying immediately (${this.retryCount}/${MAX_CODOX_RETRIES})...`);
                // Cancel the pending timeout retry and restart now.
                if (this.retryTimeout) {
                    clearTimeout(this.retryTimeout);
                    this.retryTimeout = null;
                }
                this.startCollaboration();
            }
        };
    }

    /**
     * Restores the original console.error that was saved by setupConsoleErrorInterceptor().
     * Safe to call when no interceptor is active (originalConsoleError is null).
     */
    private cleanupConsoleErrorInterceptor(): void {
        if (this.originalConsoleError) {
            console.error = this.originalConsoleError as typeof console.error;
            this.originalConsoleError = null;
        }
    }

    /**
     * Registers a Froala contentChanged listener on `ed` the first time it is called,
     * then opens the gate so the handler actively processes events. Subsequent calls
     * while the gate is already open are no-ops — the closure is only ever registered once.
     *
     * The handler mirrors the logic in StoryBasicTextEditorComponent: it checks
     * programmaticResetPending (to ignore resets during story navigation) and routes user
     * edits through the approved-story confirmation flow, then pushes HTML to contentChange$
     * so the existing debounce pipeline emits the final value.
     */
    private installFallbackContentChangedListener(ed: FroalaEditor): void {
        if (this.isFallbackListenerActive) return;
        this.isFallbackListenerActive = true;
        console.warn('StoryCollaborationTextEditor: Codox unavailable — falling back to direct Froala content listener.');

        // Register the closure once; isFallbackListenerActive gates it on every invocation.
        ed.events.on(
            'contentChanged',
            () => {
                if (!this.isFallbackListenerActive) return;
                if (this.isProcessingContentChange) return;
                if (this.programmaticResetPending) {
                    this.programmaticResetPending = false;
                    return;
                }
                if (!this.userHasInteracted) {
                    this.contentChange$.next(ed.html.get());
                    return;
                }
                this.userHasInteracted = false;
                this.confirmApprovedStoryEdit(() => this.contentChange$.next(ed.html.get()));
            },
            true
        );
    }

    /**
     * Closes the fallback listener gate so subsequent Froala contentChanged events are
     * ignored. The closure itself remains registered on the Froala instance but exits
     * immediately when isFallbackListenerActive is false. Safe to call when the listener
     * was never installed.
     */
    private removeFallbackContentChangedListener(): void {
        this.isFallbackListenerActive = false;
    }

    onProductionCommandClicked(commandType: ProductionCommandType): void {
        this.productionCommandInsertHandler(commandType);
    }
}
